import { getExplicitPrimeIcon, iconMapper } from '@/common/utils/iconMapper'

const SWING_ICON_EXTENSIONS = ['.png', '.gif', '.jpg', '.jpeg', '.svg'] as const

const swingIconModules = import.meta.glob<string>(
  '../../assets/images/swingicons/*.{png,gif,jpg,jpeg,svg}',
  { eager: true, query: '?url', import: 'default' },
)

export type PlatformIconResolution =
  | { kind: 'prime'; primeClass: string }
  | { kind: 'image'; url: string }

let swingIconCatalog: Map<string, string> | null = null

function basenameFromPath(path: string): string {
  const normalized = path.replace(/\\/g, '/')
  const idx = normalized.lastIndexOf('/')
  return idx >= 0 ? normalized.slice(idx + 1) : normalized
}

export function buildSwingIconCatalog(
  modules: Record<string, unknown> = swingIconModules,
): Map<string, string> {
  const catalog = new Map<string, string>()
  for (const [path, url] of Object.entries(modules)) {
    if (typeof url !== 'string' || !url) continue
    const basename = basenameFromPath(path)
    if (basename) catalog.set(basename.toLowerCase(), url)
  }
  return catalog
}

function getSwingIconCatalog(): Map<string, string> {
  if (!swingIconCatalog) {
    swingIconCatalog = buildSwingIconCatalog()
  }
  return swingIconCatalog
}

export function normalizePlatformIconName(name: string): string {
  return String(name ?? '')
    .trim()
    .replace(/^.*[/\\]/, '')
}

export function resolveSwingIconUrl(
  name: string,
  catalog: Map<string, string> = getSwingIconCatalog(),
): string | undefined {
  const base = normalizePlatformIconName(name)
  if (!base) return undefined

  const lower = base.toLowerCase()
  const direct = catalog.get(lower)
  if (direct) return direct

  const withoutExt = lower.replace(/\.(png|gif|jpg|jpeg|svg)$/i, '')
  for (const ext of SWING_ICON_EXTENSIONS) {
    const candidate = `${withoutExt}${ext}`
    const url = catalog.get(candidate)
    if (url) return url
  }

  return undefined
}

export function resolvePlatformIcon(name: string): PlatformIconResolution {
  const trimmed = normalizePlatformIconName(name)
  if (!trimmed) {
    return { kind: 'prime', primeClass: iconMapper.default }
  }

  const explicitPrime = getExplicitPrimeIcon(trimmed)
  if (explicitPrime) {
    return { kind: 'prime', primeClass: explicitPrime }
  }

  const swingUrl = resolveSwingIconUrl(trimmed)
  if (swingUrl) {
    return { kind: 'image', url: swingUrl }
  }

  return { kind: 'prime', primeClass: iconMapper.default }
}

export function hasPlatformIcon(name: string): boolean {
  const trimmed = normalizePlatformIconName(name)
  if (!trimmed) return false
  return Boolean(getExplicitPrimeIcon(trimmed) || resolveSwingIconUrl(trimmed))
}
